import { differenceInMinutes } from 'date-fns';

/** Minimal appointment shape for queue pulse. */
export interface QueueCandidate {
  id: string;
  patientName?: string | null;
  status: string;
  checkedInAt?: string | null;
  scheduledAt: string;
}

export type QueueFloorStatus = 'checked_in' | 'in_progress';

export interface QueuePreviewRow {
  id: string;
  patientName: string;
  status: QueueFloorStatus;
  waitMinutes: number | null;
  waitLabel: string;
}

function patientFirstName(fullName: string | null | undefined): string {
  const trimmed = fullName?.trim();
  if (!trimmed) return 'Patient';
  return trimmed.split(/\s+/)[0] ?? 'Patient';
}

function waitMinutesFor(appt: QueueCandidate, now: Date): number | null {
  const start = appt.checkedInAt ?? appt.scheduledAt;
  try {
    return Math.max(0, differenceInMinutes(now, new Date(start)));
  } catch {
    return null;
  }
}

function formatWaitLabel(minutes: number | null, status: QueueFloorStatus): string {
  if (status === 'in_progress') return 'In chair';
  if (minutes == null) return '—';
  if (minutes < 1) return 'Just now';
  return `${minutes} min`;
}

/** Patients checked in and waiting for chair. */
export function countWaitingQueue(appointments: QueueCandidate[]): number {
  return appointments.filter((a) => a.status === 'checked_in').length;
}

/** Patients currently in chair. */
export function countInChairQueue(appointments: QueueCandidate[]): number {
  return appointments.filter((a) => a.status === 'in_progress').length;
}

export function summarizeQueueFloor(appointments: QueueCandidate[]): {
  waiting: number;
  inChair: number;
  totalActive: number;
} {
  const waiting = countWaitingQueue(appointments);
  const inChair = countInChairQueue(appointments);
  return { waiting, inChair, totalActive: waiting + inChair };
}

function mapPreviewRow(appt: QueueCandidate, now: Date): QueuePreviewRow {
  const status = appt.status as QueueFloorStatus;
  const waitMinutes = waitMinutesFor(appt, now);
  return {
    id: appt.id,
    patientName: appt.patientName?.trim() || 'Patient',
    status,
    waitMinutes,
    waitLabel: formatWaitLabel(waitMinutes, status),
  };
}

/**
 * Floor preview — waiting first, then in chair (up to limit each segment combined).
 */
export function listFloorPreview(
  appointments: QueueCandidate[],
  limit = 4,
  now: Date = new Date(),
): QueuePreviewRow[] {
  const waiting = appointments
    .filter((a) => a.status === 'checked_in')
    .sort((a, b) => (a.checkedInAt ?? a.scheduledAt).localeCompare(b.checkedInAt ?? b.scheduledAt))
    .map((a) => mapPreviewRow(a, now));

  const inChair = appointments
    .filter((a) => a.status === 'in_progress')
    .sort((a, b) => (a.checkedInAt ?? a.scheduledAt).localeCompare(b.checkedInAt ?? b.scheduledAt))
    .map((a) => mapPreviewRow(a, now));

  return [...waiting, ...inChair].slice(0, limit);
}

/** @deprecated Use listFloorPreview — kept for tests migrating from waiting-only. */
export function listWaitingPreview(
  appointments: QueueCandidate[],
  limit = 3,
  now: Date = new Date(),
): QueuePreviewRow[] {
  return listFloorPreview(
    appointments.filter((a) => a.status === 'checked_in'),
    limit,
    now,
  );
}

export interface QueuePulseLabel {
  text: string;
  waiting: number;
  inChair: number;
  totalActive: number;
  leadName: string | null;
  hasLongWait: boolean;
}

/**
 * Compact trigger copy for the live clinic pulse.
 */
export function formatQueuePulseLabel(
  appointments: QueueCandidate[],
  now: Date = new Date(),
): QueuePulseLabel {
  const { waiting, inChair, totalActive } = summarizeQueueFloor(appointments);
  const waitingRows = listFloorPreview(
    appointments.filter((a) => a.status === 'checked_in'),
    1,
    now,
  );
  const hasLongWait = waitingRows.some((r) => (r.waitMinutes ?? 0) >= 30);

  if (totalActive === 0) {
    return {
      text: 'Queue · Clear',
      waiting: 0,
      inChair: 0,
      totalActive: 0,
      leadName: null,
      hasLongWait: false,
    };
  }

  const leadName = patientFirstName(
    waitingRows[0]?.patientName ??
      listFloorPreview(
        appointments.filter((a) => a.status === 'in_progress'),
        1,
        now,
      )[0]?.patientName,
  );

  const parts: string[] = ['Queue'];
  if (waiting > 0) parts.push(`${waiting} wait`);
  if (inChair > 0) parts.push(`${inChair} chair`);

  return {
    text: parts.join(' · '),
    waiting,
    inChair,
    totalActive,
    leadName,
    hasLongWait,
  };
}
